export type CommerceMaterialsViewMode = 'owner' | 'public';

export interface MaterialCommerceRow {
  itemId: number | null;
  price: number | null;
  unquoted: boolean;
  promotion: string | null;
  promotion_discount: number | null;
  value_added_tax: boolean | null;
}

export interface ParsedCommerceMaterial {
  id: string;
  name: string;
  description: string | null;
  brand: string | null;
  unit: string | null;
  catalogPrice: number | null;
  catalogUnquoted: boolean;
  commerceRow: MaterialCommerceRow | null;
}

export interface ParsedCategory {
  id: string;
  name: string;
  description: string | null;
  materials: ParsedCommerceMaterial[];
}

function num(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : NaN;
  return Number.isFinite(n) ? n : null;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : v != null ? String(v) : '';
}

function bool(v: unknown): boolean {
  return v === true;
}

function normalizeMc(raw: unknown): MaterialCommerceRow | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  return {
    itemId: num(o.id ?? o.materialCommerceId ?? o.material_commerce_id),
    price: num(o.price),
    unquoted: bool(o.unquoted),
    promotion: typeof o.promotion === 'string' ? o.promotion : o.promotion != null ? String(o.promotion) : null,
    promotion_discount: num(o.promotion_discount),
    value_added_tax: typeof o.value_added_tax === 'boolean' ? o.value_added_tax : null,
  };
}

function pickCommerceLink(
  materialsCommerce: unknown,
  commerceId: number | null
): MaterialCommerceRow | null {
  if (!Array.isArray(materialsCommerce) || materialsCommerce.length === 0) return null;
  if (commerceId != null && Number.isFinite(commerceId)) {
    for (const item of materialsCommerce) {
      if (item == null || typeof item !== 'object') continue;
      const c = (item as Record<string, unknown>).commerce;
      if (c != null && typeof c === 'object' && !Array.isArray(c)) {
        const cid = num((c as Record<string, unknown>).id);
        if (cid === commerceId) return normalizeMc(item);
      }
    }
  }
  return normalizeMc(materialsCommerce[0]);
}

function parseMaterial(o: Record<string, unknown>, commerceId: number | null): ParsedCommerceMaterial {
  return {
    id: str(o.id),
    name: str(o.name).trim() || '—',
    description: typeof o.description === 'string' ? o.description : null,
    brand: typeof o.brand === 'string' && o.brand.trim() ? o.brand.trim() : null,
    unit: typeof o.unit === 'string' && o.unit.trim() ? o.unit.trim() : null,
    catalogPrice: num(o.price),
    catalogUnquoted: bool(o.unquoted),
    commerceRow: pickCommerceLink(o.materials_commerce, commerceId),
  };
}

export function parseCommerceMaterialsPayload(
  data: unknown,
  commerceId: number | null
): ParsedCategory[] {
  if (!Array.isArray(data)) return [];
  const out: ParsedCategory[] = [];
  for (const cat of data) {
    if (cat == null || typeof cat !== 'object' || Array.isArray(cat)) continue;
    const c = cat as Record<string, unknown>;
    const id = str(c.id);
    const name = str(c.name).trim();
    if (!name) continue;
    const materialsRaw = c.materials;
    const materials: ParsedCommerceMaterial[] = [];
    if (Array.isArray(materialsRaw)) {
      for (const m of materialsRaw) {
        if (m == null || typeof m !== 'object' || Array.isArray(m)) continue;
        materials.push(parseMaterial(m as Record<string, unknown>, commerceId));
      }
    }
    out.push({
      id: id || name,
      name,
      description: typeof c.description === 'string' ? c.description : null,
      materials,
    });
  }
  return out;
}

export function parseCatalogMaterialItemsToParsedCategories(
  items: unknown[],
  commerceId: number | null
): ParsedCategory[] {
  if (!Array.isArray(items) || items.length === 0) return [];
  type Bucket = { id: string; name: string; materials: ParsedCommerceMaterial[] };
  const byCat = new Map<string, Bucket>();

  for (const raw of items) {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const o = raw as Record<string, unknown>;
    const catId = str(o.categoryId ?? o.category_id ?? '');
    const catNameRaw = str(o.categoryName ?? o.category_name ?? '').trim();
    const catName = catNameRaw || 'Sin categoría';
    const key = catId || catName;
    if (!byCat.has(key)) {
      byCat.set(key, { id: catId || key, name: catName, materials: [] });
    }
    const bucket = byCat.get(key)!;
    const matId = str(o.materialId ?? o.material_id ?? '');
    const matName = str(o.materialName ?? o.material_name ?? o.name ?? '').trim() || '—';
    const row: MaterialCommerceRow = {
      itemId: num(o.materialCommerceId ?? o.material_commerce_id ?? o.itemId),
      price: num(o.price),
      unquoted: bool(o.unquoted),
      promotion: typeof o.promotion === 'string' ? o.promotion : o.promotion != null ? String(o.promotion) : null,
      promotion_discount: num(o.promotion_discount ?? o.promotionDiscount),
      value_added_tax:
        typeof o.value_added_tax === 'boolean'
          ? o.value_added_tax
          : typeof o.valueAddedTax === 'boolean'
            ? o.valueAddedTax
            : null,
    };
    const hasCommerce = row.itemId != null && row.itemId > 0;
    bucket.materials.push({
      id: matId || matName,
      name: matName,
      description: typeof o.description === 'string' ? o.description : null,
      brand: typeof o.brand === 'string' && o.brand.trim() ? o.brand.trim() : null,
      unit: typeof o.unit === 'string' && o.unit.trim() ? o.unit.trim() : null,
      catalogPrice: num(o.price),
      catalogUnquoted: bool(o.unquoted),
      commerceRow: hasCommerce ? row : null,
    });
  }

  return [...byCat.values()].map((b) => ({
    id: b.id,
    name: b.name,
    description: null,
    materials: b.materials,
  }));
}

export function extractCommerceMaterialsDataArray(resBody: unknown): unknown {
  if (resBody == null || typeof resBody !== 'object') return null;
  const o = resBody as Record<string, unknown>;
  const d = o.data;
  if (Array.isArray(d)) return d;
  return null;
}

export interface FlatMaterialRow {
  categoryId: string;
  categoryName: string;
  material: ParsedCommerceMaterial;
}

export function flattenCategories(categories: ParsedCategory[]): FlatMaterialRow[] {
  const rows: FlatMaterialRow[] = [];
  for (const cat of categories) {
    for (const material of cat.materials) {
      rows.push({
        categoryId: cat.id,
        categoryName: cat.name,
        material,
      });
    }
  }
  return rows;
}

export function filterFlatRows(
  rows: FlatMaterialRow[],
  opts: { q: string; categoryId: string | 'all' }
): FlatMaterialRow[] {
  const q = opts.q.trim().toLowerCase();
  return rows.filter((r) => {
    if (opts.categoryId !== 'all' && r.categoryId !== opts.categoryId) return false;
    if (!q) return true;
    const m = r.material;
    const hay = [
      r.categoryName,
      m.name,
      m.brand ?? '',
      m.description ?? '',
      m.unit ?? '',
    ]
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
}
